import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import CustomerService from "../../api/customer";

// APP COMPONENTS
import RemoveModal from "../modals/removeModal";
import CustomerRow from "./customerRow";
import NameFilter from "../search/nameFilter";
import PhoneFilter from "../search/phoneFilter";
import SearchFilter from "../search/searchFilter";
import NewCustomerModal from "./newCustomerModal";
import ImportCustomersExcelFileModal from "./importCustomersExcelFileModal";
import PageHeaderLight from "../ui/PageHeaderLight";
import BlacklistedFilter from "../search/BlacklistedFilter";

// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

// ICONS
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CloseIcon from "@mui/icons-material/Close";

// --- Animation Variants ---

const fadeIn = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 400, damping: 30 },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.1 },
  },
};

const listItem = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 500, damping: 35 },
  },
  exit: {
    opacity: 0,
    x: -16,
    transition: { duration: 0.15 },
  },
};

// --- Skeleton Loader ---

const CustomerListSkeleton = () => (
  <Box>
    {[...Array(6)].map((_, i) => (
      <Box key={i} sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <Skeleton variant="circular" width={44} height={44} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="45%" height={24} />
          <Skeleton variant="text" width="28%" height={18} />
        </Box>
        <Skeleton variant="rectangular" width={64} height={32} sx={{ borderRadius: 1 }} />
      </Box>
    ))}
  </Box>
);

// --- Component ---

export default function Customers() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [filters, setFilters] = useState({
    newSearch: false,
    name: "",
    phone: "",
    blacklisted: false,
  });
  const [showButtonMore, setShowButtonMore] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [showImportExcelModal, setShowImportExcelModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [expandedFilters, setExpandedFilters] = useState(true);

  // Derived: active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.name) count++;
    if (filters.phone) count++;
    if (filters.blacklisted) count++;
    return count;
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  // --- Effects ---

  useEffect(() => {
    loadCustomers(true);
  }, []);

  // --- Handlers ---

  const search = async (filtersData) => {
    setPage(1);
    try {
      const filtersObj = {
        name: filtersData[NameFilter.name] ?? "",
        phone: filtersData[PhoneFilter.name] ?? "",
        blacklisted: filtersData[BlacklistedFilter.name]?.value ?? "",
      };
      setFilters(filtersObj);
      await loadCustomers(true, filtersObj);
    } catch (err) {
      setError("Erro ao filtrar clientes. Tente novamente.");
    }
  };

  const loadCustomers = async (newSearch, filtersData = null) => {
    try {
      setLoading(newSearch);
      setRefreshing(true);
      setError(null);

      let pageQuery = page;
      if (newSearch) {
        pageQuery = 1;
        setPage(1);
      }

      const params = {
        page: pageQuery,
        name: filtersData ? filtersData.name : filters.name,
        phone: filtersData ? filtersData.phone : filters.phone,
        blacklisted: filtersData ? filtersData.blacklisted : filters.blacklisted,
      };
      const {
        data: { results },
      } = await CustomerService.getCustomers(params);

      let result;
      if (newSearch) {
        result = [...results];
        switchShowButtonMore(results.length);
      } else {
        const new_customers = results.filter(
          ({ customer_id: id1 }) =>
            !customers.some(({ customer_id: id2 }) => id2 === id1)
        );
        result = [...customers, ...new_customers];
        switchShowButtonMore(new_customers.length);
      }

      setPage(pageQuery + 1);
      setCustomers(result);
    } catch (err) {
      setError("Erro ao carregar clientes. Tente novamente.");
      console.error("Error loading customers:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const switchShowButtonMore = (length) => {
    setShowButtonMore(length > 0);
  };

  const handleDeleteCustomerModal = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteModal(!showDeleteModal);
  };

  const handleDeleteCustomer = async () => {
    try {
      await CustomerService.deleteCustomer(customerToDelete.customer_id);
      setCustomerToDelete(null);
      setShowDeleteModal(false);
      await loadCustomers(true);
    } catch (err) {
      setError("Erro ao excluir cliente. Tente novamente.");
    }
  };

  const handleNewCustomerModal = (e, customer = null) => {
    setEditingCustomer(customer);
    setShowNewCustomerModal(!showNewCustomerModal);
  };

  const handleImportExcelModal = () => {
    setShowImportExcelModal(!showImportExcelModal);
  };

  const handleCustomerAction = async (isEditing, customer) => {
    try {
      if (isEditing) {
        await editCustomer(customer);
      } else {
        await createCustomer(customer);
      }
      setShowNewCustomerModal(false);
      await loadCustomers(true);
    } catch (err) {
      setError("Erro ao salvar cliente. Tente novamente.");
    }
  };

  const createCustomer = async (customer) => {
    try {
      await CustomerService.createCustomer(customer);
    } catch (err) {
      console.error("Error creating customer:", err);
      throw err;
    }
  };

  const editCustomer = async (customer) => {
    try {
      const { customer_id } = customer;
      await CustomerService.editCustomer(customer, customer_id);
    } catch (err) {
      console.error("Error editing customer:", err);
      throw err;
    }
  };

  const handleRefresh = () => {
    loadCustomers(true);
  };

  const toggleExpandedFilters = () => {
    setExpandedFilters(!expandedFilters);
  };

  const clearFilters = () => {
    setFilters({ newSearch: false, name: "", phone: "", blacklisted: false });
    loadCustomers(true, { name: "", phone: "", blacklisted: false });
  };

  // --- Page Header ---

  const pageHeaderActions = [
    {
      key: "filtros",
      variant: expandedFilters ? "contained" : "outlined",
      startIcon: <FilterListIcon />,
      onClick: toggleExpandedFilters,
      children: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Filtros
          {activeFilterCount > 0 && !expandedFilters && (
            <Chip
              label={activeFilterCount}
              size="small"
              sx={{
                height: 20,
                minWidth: 20,
                fontSize: "0.7rem",
                fontWeight: 700,
                bgcolor: theme.palette.primary.main,
                color: "white",
              }}
            />
          )}
        </Box>
      ),
    },
    {
      key: "novo-cliente",
      variant: "contained",
      startIcon: <PersonAddIcon />,
      onClick: () => handleNewCustomerModal(null, null),
      children: "Novo Cliente",
    },
    {
      key: "importar",
      variant: "outlined",
      startIcon: <UploadFileIcon />,
      onClick: handleImportExcelModal,
      children: "Importar",
    },
  ];

  // --- Empty State ---

  const renderEmptyState = () => (
    <motion.div variants={fadeIn} initial="hidden" animate="visible">
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          {hasActiveFilters ? (
            <SearchOffIcon
              sx={{ fontSize: 36, color: theme.palette.text.disabled }}
            />
          ) : (
            <PeopleAltIcon
              sx={{ fontSize: 36, color: theme.palette.primary.main }}
            />
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}
        >
          {hasActiveFilters
            ? "Nenhum cliente encontrado com esses filtros"
            : "Nenhum cliente cadastrado"}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: 420,
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {hasActiveFilters
            ? "Tente ajustar os filtros para encontrar o que está procurando."
            : "Cadastre seus clientes para facilitar o gerenciamento de pedidos e entregas."}
        </Typography>

        <Button
          variant="contained"
          startIcon={hasActiveFilters ? <FilterListIcon /> : <PersonAddIcon />}
          onClick={hasActiveFilters ? toggleExpandedFilters : () => handleNewCustomerModal()}
          sx={{ borderRadius: 2, px: 3, py: 1 }}
        >
          {hasActiveFilters ? "Ajustar Filtros" : "Adicionar Novo Cliente"}
        </Button>
      </Box>
    </motion.div>
  );

  // --- Render ---

  return (
    <>
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1400, mx: "auto" }}>
        {/* Header */}
        <PageHeaderLight
          title="Clientes"
          subtitle="Gerencie seus clientes e veja o histórico de pedidos"
          actions={pageHeaderActions}
          filters={
            <SearchFilter
              title=""
              filters={[NameFilter, PhoneFilter, BlacklistedFilter]}
              submit={search}
              showHeader={false}
              initialExpanded
            />
          }
          expandedFilters={expandedFilters}
          toggleFilters={toggleExpandedFilters}
        />

        {/* Active Filters Bar */}
        {!loading && hasActiveFilters && (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label="Limpar filtros"
              variant="outlined"
              size="small"
              onDelete={clearFilters}
              sx={{ borderRadius: 1.5 }}
            />
          </Box>
        )}

        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Alert
                severity="error"
                variant="standard"
                icon={<ErrorOutlineIcon />}
                action={
                  <IconButton
                    aria-label="Fechar alerta de erro"
                    color="inherit"
                    size="small"
                    onClick={() => setError(null)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {error}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={handleRefresh}
                    sx={{ ml: 1, textTransform: "none", fontWeight: 600 }}
                  >
                    Tentar novamente
                  </Button>
                </Box>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12}>
            {loading ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                <CustomerListSkeleton />
              </motion.div>
            ) : customers.length === 0 ? (
              renderEmptyState()
            ) : (
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                <AnimatePresence>
                  {customers.map((customer) => (
                    <motion.div
                      key={customer.customer_id}
                      variants={listItem}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <CustomerRow
                        customer={customer}
                        deleteAction={() => handleDeleteCustomerModal(customer)}
                        editAction={() => handleNewCustomerModal(null, customer)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Load More Button */}
                <Button
                  title="Carregar mais"
                  size="large"
                  fullWidth
                  disabled={!showButtonMore || refreshing}
                  startIcon={
                    refreshing ? (
                      <CircularProgress size={18} />
                    ) : (
                      <ExpandCircleDownIcon />
                    )
                  }
                  onClick={() => loadCustomers(false)}
                  sx={{
                    mt: 2,
                    py: 1.2,
                    borderRadius: 2,
                    color: theme.palette.text.secondary,
                    bgcolor: theme.palette.grey[50],
                    border: `1px solid ${theme.palette.grey[200]}`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: theme.palette.grey[100],
                      borderColor: theme.palette.grey[300],
                    },
                    "&.Mui-disabled": {
                      color: alpha(theme.palette.text.secondary, 0.4),
                      bgcolor: alpha(theme.palette.grey[100], 0.5),
                    },
                  }}
                >
                  {refreshing
                    ? "Carregando..."
                    : showButtonMore
                    ? "Mostrar Mais Clientes"
                    : "Todos os clientes foram carregados"}
                </Button>
              </motion.div>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Modals */}
      <RemoveModal
        title="Excluir Cliente"
        message={`Deseja excluir o cliente "${customerToDelete?.name}"? Esta ação não pode ser desfeita.`}
        show={showDeleteModal}
        toggleModal={handleDeleteCustomerModal}
        onRemove={handleDeleteCustomer}
      />

      {showNewCustomerModal && (
        <NewCustomerModal
          customer={editingCustomer}
          closeModal={handleNewCustomerModal}
          show={showNewCustomerModal}
          createCustomer={handleCustomerAction}
        />
      )}

      {showImportExcelModal && (
        <ImportCustomersExcelFileModal
          show={showImportExcelModal}
          closeModal={handleImportExcelModal}
          loadCustomers={loadCustomers}
        />
      )}
    </>
  );
}
